import type { Ruleset } from "./types.js";

/**
 * MIS 3005-D:2025 Table 1 — internal design temperatures, taken from CIBSE Guide A.
 *
 * Clause 3.4.1(a): heat load calculations use internal temperatures "not less than
 * those specified in Table 1". These are therefore MINIMA — a designer may exceed
 * them (e.g. at customer request) but never go below.
 */
export type RoomType =
  | "living-room"
  | "dining-room"
  | "bedsitting-room"
  | "bedroom"
  | "hall-landing"
  | "kitchen"
  | "bathroom"
  | "toilet";

export const MIS_3005_D_TABLE_1: Ruleset<Record<RoomType, number>> = {
  id: "mcs.internal-design-temperatures",
  version: "1.0.0",
  effectiveFrom: "2025-01-01",
  jurisdictions: ["england", "wales", "scotland", "northern-ireland"],
  provenance: "verified",
  source: {
    standard: "MIS 3005-D:2025",
    issue: "2.0",
    clause: "Table 1",
    issued: "2025-12-05",
    url: "https://mcscertified.com/wp-content/uploads/2025/12/MIS-3005-D-2025-V2.0-Final.pdf",
  },
  data: {
    "living-room": 21,
    "dining-room": 21,
    "bedsitting-room": 21,
    bedroom: 18,
    "hall-landing": 18,
    kitchen: 18,
    bathroom: 22,
    toilet: 18,
  },
};

/**
 * MIS 3005-D:2025 Table 2 — outside design temperatures, from CIBSE Guide A Table 2.5.
 *
 * Column A is the temperature equalled or exceeded for 99% of hours in a year;
 * column B for 99.6%. Clause 3.4.1(a): "If column B is selected, no uplift factor
 * for intermittent heating is required."
 *
 * NOTE: the standard tabulates only these eight locations. Real designs need the
 * nearest/most representative location for the site — a postcode→location mapping
 * is a separate dataset (see docs/adr/0002) and is NOT part of the standard itself.
 */
export interface ExternalDesignTemperature {
  readonly location: string;
  /** Metres above sea level, as tabulated. */
  readonly altitudeM: number;
  /** Column A — 99% of hours (°C). */
  readonly columnA: number;
  /** Column B — 99.6% of hours (°C). */
  readonly columnB: number;
}

export const MIS_3005_D_TABLE_2: Ruleset<readonly ExternalDesignTemperature[]> = {
  id: "mcs.external-design-temperatures",
  version: "1.0.0",
  effectiveFrom: "2025-01-01",
  jurisdictions: ["england", "wales", "scotland", "northern-ireland"],
  provenance: "verified",
  source: {
    standard: "MIS 3005-D:2025",
    issue: "2.0",
    clause: "Table 2",
    issued: "2025-12-05",
    url: "https://mcscertified.com/wp-content/uploads/2025/12/MIS-3005-D-2025-V2.0-Final.pdf",
  },
  data: [
    { location: "Belfast", altitudeM: 68, columnA: -1.5, columnB: -3.2 },
    { location: "Birmingham", altitudeM: 96, columnA: -3.2, columnB: -5.1 },
    { location: "Cardiff", altitudeM: 67, columnA: -1.5, columnB: -3.1 },
    { location: "Edinburgh", altitudeM: 35, columnA: -3.2, columnB: -5.4 },
    { location: "Glasgow", altitudeM: 5, columnA: -3.5, columnB: -5.9 },
    { location: "London", altitudeM: 25, columnA: -1.7, columnB: -3.0 },
    { location: "Manchester", altitudeM: 75, columnA: -2.7, columnB: -4.5 },
    { location: "Plymouth", altitudeM: 27, columnA: -0.2, columnB: -1.5 },
  ],
};

export function externalDesignTemperature(
  location: string,
  column: "A" | "B",
): number {
  const row = MIS_3005_D_TABLE_2.data.find(
    (r) => r.location.toLowerCase() === location.toLowerCase(),
  );
  if (!row) {
    throw new Error(
      `"${location}" is not tabulated in MIS 3005-D Table 2. ` +
        `Available: ${MIS_3005_D_TABLE_2.data.map((r) => r.location).join(", ")}`,
    );
  }
  return column === "A" ? row.columnA : row.columnB;
}
